import api from './api';

export interface Contract {
  id: number;
  contractName: string;
  contractDate: string;
  supplierName: string;
  productName: string;
  contractType: 'Volume' | 'Price' | 'BaisseClause';
  startDate: string;
  endDate: string;
  totalVolume: number;
  businessUnitMode: 'Single' | 'Several';
  // Fields for Single business unit mode
  businessUnitId?: number;
  businessUnitName?: string;
  incoTerm?: string;
  incoTermPlace?: string;
  pricePerKg?: number;
  currency?: string;
  confirmedBy?: string;
  // Common fields
  shippingInstructions: string;
  volumeDistributions?: VolumeDistribution[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface VolumeDistribution {
  id: number;
  businessUnitId: number;
  businessUnitName: string;
  productName: string;
  supplierName: string;
  volumeKgs: number;
  incoTerm: string;
  incoTermPlace: string;
  pricePerKg: number;
  currency: string;
  shippingInstructions: string;
  confirmedBy?: string;
}

export interface CreateContractRequest {
  contractDate: string;
  supplierName: string;
  productName: string;
  contractType: string;
  startDate: string;
  endDate: string;
  volumeKgs: number;
  businessUnitMode: string;
  businessUnitId?: number;
  incoTerm?: string;
  incoTermPlace?: string;
  pricePerKg?: number;
  currency?: string;
  confirmedBy?: string;
  shippingInstructions: string;
  volumeDistributions?: {
    businessUnitId: number;
    productName: string;
    supplierName: string;
    volumeKgs: number;
    incoTerm: string;
    incoTermPlace: string;
    pricePerKg: number;
    currency: string;
    shippingInstructions: string;
    confirmedBy?: string;
  }[];
}

export interface UpdateContractRequest {
  contractDate?: string;
  supplierName?: string;
  productName?: string;
  contractType?: string;
  startDate?: string;
  endDate?: string;
  volumeKgs?: number;
  incoTerm?: string;
  incoTermPlace?: string;
  pricePerKg?: number;
  currency?: string;
  confirmedBy?: string;
  shippingInstructions?: string;
}

export interface UpdateVolumeDistributionRequest {
  productName?: string;
  supplierName?: string;
  volumeKgs?: number;
  incoTerm?: string;
  incoTermPlace?: string;
  pricePerKg?: number;
  currency?: string;
  shippingInstructions?: string;
  confirmedBy?: string;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface GetContractsParams {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  businessUnitId?: number;
}

export const contractService = {
  getAll: (params?: GetContractsParams) =>
    api.get<PagedResult<Contract>>('/contracts', { params }),
  
  getById: (id: number) =>
    api.get<Contract>(`/contracts/${id}`),
  
  create: (data: CreateContractRequest) =>
    api.post<Contract>('/contracts', data),
  
  update: (id: number, data: UpdateContractRequest) =>
    api.put<Contract>(`/contracts/${id}`, data),
  
  updateVolumeDistribution: (id: number, data: UpdateVolumeDistributionRequest) =>
    api.put<VolumeDistribution>(`/contracts/volume-distributions/${id}`, data),
  
  delete: (id: number) =>
    api.delete(`/contracts/${id}`),
  
  uploadDocument: (contractId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/contracts/${contractId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  getDocuments: (contractId: number) =>
    api.get(`/contracts/${contractId}/documents`),
  
  downloadDocument: (documentId: number) =>
    api.get(`/contracts/documents/${documentId}/download`, {
      responseType: 'blob',
    }),
};
